import {Button} from "../../../Form/Button/Button";
import {buttonColors} from "../../../Pickers/InputTheme";
import React, {useContext, useEffect, useMemo, useState} from "react";
import {Modal} from "../../../Smart/Modal/Modal";
import {SelectBox} from "../../../Pickers/SelectBox/SelectBox";
import {SearchableSelectBox} from "../../../Pickers/SearchableSelectBox/SearchableSelectBox";
import './SendNotificationsModal.scss'
import {getCompanySMSQuotaAPI, sendTaskReminderAPI, userCountToSendSMSAPI} from "../../../../Services/api";
import LoadingSpinner from "../../../LoadingSpinner/LoadingSpinner";
import {UserReactContext} from "../../../../contexts/UserContext";
import {AlertDialog} from "../../../Smart/AlertDialog/AlertDialog";
import {
    NotificationSuccessAlertMessageBody
} from "../NotificationSuccessAlertMessageBody/NotificationSuccessAlertMessageBody";
import bellIcon from '../../../../assets/images/notification-bell.png'
import {MessageTypeSelectionButtons} from "../../MessageTypeSelectionButtons/MessageTypeSelectionButtons";
import {useUserAdminOrgs} from "../../../Hooks/UseUserAdminOrgs";
import {SendNotificationCounts} from "../../SendNotificationCounts/SendNotificationCounts";
import {CompanyReactContext} from "../../../../contexts/CompanyContext";
import {EntityTypes} from "../../NotificationsLib";
import {getTaskOptionsForNotificationsInOrganization} from "../../../Settings/Reminders/RemindersLib";
import {MessageTypes} from "../../../../Utils/Constants";
import {I18nReactContext} from "../../../../contexts/I18nContext";
import {filter, find, get} from "lodash";
import Tooltip from "@mui/material/Tooltip";


const getSendOptions = ({i18n}) => [{
    value: '', text: ''
},
    {
        value: 'allWithAnyOpenTasks', text: i18n.translate('sendSMSModal.sendToOptions.allWithAnyOpenTasks')
    },
    {
        value: 'all', text: i18n.translate('sendSMSModal.sendToOptions.all')
    },
    {
        value: 'knowledgeCenter', text: i18n.translate('sendSMSModal.sendToOptions.knowledgeCenter')
    },
    // {
    //     value: 'onlyNotNotified', text: i18n.translate('sendSMSModal.sendToOptions.onlyNotNotified')
    // }

]


export const SendNotificationsModal = ({sendSMSModalOpen, closeSendSMSModal}) => {
    const {adminOrganizationOptions, adminOrganizations, isOrgsLoading} = useUserAdminOrgs({
        withAllOption: true,
        withCoursesAndSignDocs: true
    })
    const {i18n} = useContext(I18nReactContext)
    const [selectedSentToOption, setSelectedSentToOption] = useState()
    const [signDocsOptions, setSignDocsOptions] = useState([])
    const [selectedTaskOption, setSelectedTaskOption] = useState()
    const [loadingSMSParameters, setLoadingSMSParameters] = useState(false)
    const [usersToSend, setUsersToSend] = useState({})
    const [companySMSQuota, setCompanySMSQuota] = useState(null)
    const [showSuccessAlert, setShowSuccessAlert] = useState(false)
    const [successAlertData, setSuccessAlertData] = useState(null)
    const [showFailedAlert, setShowFailedAlert] = useState(false)
    const [alertMessage, setAlertMessage] = useState('')
    const [isSending, setIsSending] = useState(false)
    const [selectedMessagingTypes, setSelectedMessagingTypes] = useState([MessageTypes.SMS])
    const [selectedOrgOption, setSelectedOrgOption] = useState('all')
    let memoizedOptions = useMemo(() => getSendOptions({i18n}), [i18n])

    const filteredOrganizationOptions = useMemo(() => {
        if (!adminOrganizationOptions || !adminOrganizations || selectedSentToOption !== 'knowledgeCenter') {
            return adminOrganizationOptions
        }

        // Filter organizations that have at least one doNotForceCompletion SignDoc
        const orgsWithDoNotForceCompletion = filter(adminOrganizations, (org) => {
            const orgSignDocs = get(org, 'organization.signDocs', [])
            return orgSignDocs.some((signDoc) => {
                const orgSignDoc = find(signDoc.organizationSignDoc, {OrganizationId: get(org, 'organization.id')})
                return orgSignDoc?.isValid && signDoc.doNotForceCompletion === true
            })
        })

        // Convert to options format and add "all" option if there are organizations with doNotForceCompletion SignDocs
        let filteredOptions = orgsWithDoNotForceCompletion.map((org) => ({
            value: get(org, 'organization.id'),
            orgLogo: get(org, 'organization.orgLogo'),
            text: get(org, 'organization.orgName')
        }))

        if (filteredOptions.length > 0) {
            if (filteredOptions.length < 50) {
                filteredOptions.unshift({value: 'all', text: i18n.translate('allOrganizations')})
            } else {
                filteredOptions.push({value: 'all', text: i18n.translate('allOrganizations')})
            }
        }

        return filteredOptions
    }, [adminOrganizationOptions, adminOrganizations, selectedSentToOption, i18n])

    const userContext = useContext(UserReactContext)
    const companyContext = useContext(CompanyReactContext)
    const onSelectedSentToOptionChange = (e) => {
        setSelectedSentToOption(e.target.value)
        // Reset organization selection when switching to knowledge center if current org doesn't have doNotForceCompletion SignDocs
        if (e.target.value === 'knowledgeCenter') {
            setSelectedOrgOption('all')
        }
    }
    const onSelectedCourseOptionChange = (e) => {
        setSelectedTaskOption(e.target.value)
    }
    const onSelectedOrgOptionChange = (e) => {
        setSelectedOrgOption(e.target.value)
    }

    const canSend = () => {
        return ((selectedSentToOption && selectedTaskOption && (selectedOrgOption || selectedOrgOption === "") && selectedTaskOption !== '') || selectedSentToOption === 'allWithAnyOpenTasks')
            && !loadingSMSParameters && selectedSentToOption !== ''
    }

    useEffect(() => {
        loadSMSParameters()
    }, [selectedSentToOption, selectedTaskOption]);

    useEffect(() => {
        if (selectedSentToOption === 'allWithAnyOpenTasks') {
            loadSMSParameters()
        }
    }, [selectedOrgOption]);

    const loadSMSParameters = async () => {
        if (!canSend()) {
            return
        }
        setLoadingSMSParameters(true)
        const skipAlreadyNotifiedUsers = selectedSentToOption === 'onlyNotNotified'
        const entityType = EntityTypes.SIGN_DOC
        const {sendToUsersWithPhone, sendToUsersWithEmail, totalUsersCount} = await userCountToSendSMSAPI({
            entityId: selectedTaskOption,
            entityType,
            organizationId: selectedOrgOption,
            skipAlreadyNotifiedUsers,
            allUsersWithOpenTask: selectedSentToOption === 'allWithAnyOpenTasks'
        })
        const {remainingQuota} = await getCompanySMSQuotaAPI({companyId: userContext.getUserCompanyId()})
        setCompanySMSQuota(remainingQuota)
        setUsersToSend({sendToUsersWithPhone, sendToUsersWithEmail, totalUsersCount})
        setLoadingSMSParameters(false)
    }


    const loadSignDocsOptions = async () => {
        let {signDocOptionsTmp} = await getTaskOptionsForNotificationsInOrganization({
            selectedOrgOption,
            adminOrganizations,
            showPastSignDocs: true,
            i18n,
            doNotforceCompletion: selectedSentToOption === 'knowledgeCenter'
        });
        setSignDocsOptions(signDocOptionsTmp)
    }

    useEffect(() => {
        loadSignDocsOptions()
        setSelectedTaskOption("")
    }, [selectedOrgOption, adminOrganizationOptions, adminOrganizations, selectedSentToOption])

    // useEffect(() => {
    //     setsignDocsOptions()
    //     setSelectedTaskOption("")
    // }, [selectedOrgOption, adminOrganizationOptions, adminOrganizations])


    const sendSMSToUsers = async () => {
        setIsSending(true)
        const entityType = EntityTypes.SIGN_DOC
        try {
            const {sentMessages, notSentMessages} = await sendTaskReminderAPI({
                entityId: selectedTaskOption,
                entityType,
                organizationId: selectedOrgOption,
                allUsersWithOpenTask: selectedSentToOption === 'allWithAnyOpenTasks',
                skipAlreadyNotifiedUsers: selectedSentToOption === 'onlyNotNotified',
                messagingTypes: selectedMessagingTypes
            })
            setSuccessAlertData({sentMessages, notSentMessages})
            setShowSuccessAlert(true)
        } catch (err) {
            const errorType = get(err, 'error', 'general_error')
            setAlertMessage(i18n.translate(`CourseReminderNotificationButton.alerts.${errorType}`))
            setShowFailedAlert(true)
        } finally {
            setIsSending(false)
            companyContext.refreshCompanyQuota()
        }
    }

    const clearAndCloseAlerts = () => {
        setShowSuccessAlert(false)
        setShowFailedAlert(false)
        setAlertMessage('')
        closeSendSMSModal()
    }
    const messagesToSendCount = useMemo(() => {
        let count = 0
        const {sendToUsersWithPhone = 0, sendToUsersWithEmail = 0} = usersToSend || {}
        if (selectedMessagingTypes.includes(MessageTypes.SMS)) count += sendToUsersWithPhone
        if (selectedMessagingTypes.includes(MessageTypes.EMAIL)) count += sendToUsersWithEmail
        if (selectedMessagingTypes.includes(MessageTypes.WHATSAPP)) count += sendToUsersWithPhone
        return count
    }, [usersToSend, selectedMessagingTypes])

    const isQuotaExceeded = companySMSQuota !== null && messagesToSendCount > 0 && companySMSQuota < messagesToSendCount

    const hasError = () => {
        return !canSend() || !messagesToSendCount || isQuotaExceeded
    }
    const onSelectedMessagingTypeChange = (messagingType) => {
        //if it is the only one selected do not remove it

        if (selectedMessagingTypes.includes(messagingType) && selectedMessagingTypes.length > 1) {
            setSelectedMessagingTypes(selectedMessagingTypes.filter((type) => type !== messagingType))
        } else {
            let messagingTypes = [...selectedMessagingTypes, messagingType]
            //uniq array
            messagingTypes = messagingTypes.filter((value, index, self) => self.indexOf(value) === index)
            setSelectedMessagingTypes(messagingTypes)
        }
    }

    return <Modal
        open={sendSMSModalOpen}
        onClose={closeSendSMSModal}>
        {isSending ? <LoadingSpinner/> : ''}
        <div className="sendSMSModal modalWrapper">
            <div className="modalTitle">
                <div>{i18n.translate('sendSMSModal.title')}</div>
            </div>
            <div className="modalContent">
                <div className="selectBoxes">
                    <SelectBox options={memoizedOptions} value={selectedSentToOption}
                               label={i18n.translate('sendSMSModal.sendToOptions.label')}
                               onChange={onSelectedSentToOptionChange}/>
                    <div className="selectCourseAndOrg">
                        {filteredOrganizationOptions ?
                            <SelectBox options={filteredOrganizationOptions} value={selectedOrgOption}
                                       label={i18n.translate('sendSMSModal.organizationOptions.label')}
                                       onChange={onSelectedOrgOptionChange}/> : ""}

                        {selectedSentToOption !== 'allWithAnyOpenTasks' && signDocsOptions ?
                            <SearchableSelectBox options={signDocsOptions} value={selectedTaskOption}
                                                 label={i18n.translate('sendSMSModal.taskOptions.label')}
                                                 dataTestId="send-reminder-task-select"
                                                 onChange={onSelectedCourseOptionChange}/> : ""}
                    </div>
                    <div className="messagingTypeWrapper">
                        <MessageTypeSelectionButtons onSelectedMessageTypeChange={onSelectedMessagingTypeChange}
                                                     selectedMessagingTypes={selectedMessagingTypes}/>
                    </div>
                </div>

                <SendNotificationCounts canSend={canSend} loadingSMSParameters={loadingSMSParameters}
                                        usersToSend={usersToSend}
                                        messagingTypes={selectedMessagingTypes}
                                        companySMSQuota={companySMSQuota} selectedSentToOption={selectedSentToOption}/>
            </div>
            <div className="modalFooter">
                <Button
                    color={buttonColors.GREY}
                    onClick={closeSendSMSModal}
                    text={i18n.translate('general.cancel')}
                />
                <Tooltip
                    title={isQuotaExceeded ? i18n.translate('CourseReminderNotificationButton.alerts.company_has_no_sms_quota') : ''}
                    arrow
                >
                    <span>
                        <Button
                            color={buttonColors.YELLOW}
                            onClick={sendSMSToUsers}
                            disabled={hasError()}
                            text={i18n.translate('sendSMSModal.send')}
                        />
                    </span>
                </Tooltip>

            </div>
        </div>
        {showSuccessAlert ?
            <AlertDialog
                title={<div className="alertReminderTitle">
                    {i18n.translate('CourseReminderNotificationButton.alerts.reminderSent')}
                    <img src={bellIcon} alt=""/>
                </div>}
                alertMessage={<NotificationSuccessAlertMessageBody {...successAlertData}/>}
                onOk={clearAndCloseAlerts}/> : ''}
        {showFailedAlert ?
            <AlertDialog title={i18n.translate('CourseReminderNotificationButton.alerts.reminderNotSent')}
                         alertMessage={alertMessage}
                         onOk={clearAndCloseAlerts}/> : ''}
    </Modal>
}